import { inject, Injectable, Injector } from '@angular/core';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';
import { ConfigService } from './config';
import {
  extractDeepLinkFromFcmData,
  parseDeepLinkRaw,
} from '../helpers/push-payload';
import type { PushNotificationData } from '../models/push-notification';
import { NotificationNavigationService } from './notification-navigation';

const SW_PATH = '/firebase-messaging-sw.js';

@Injectable({ providedIn: 'root' })
export class FirebaseMessagingService {
  #config = inject(ConfigService);
  #injector = inject(Injector);
  #app: FirebaseApp | null = null;
  #messaging: Messaging | null = null;
  #listening = false;

  async isSupported(): Promise<boolean> {
    return isSupported();
  }

  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      return null;
    }
    try {
      return await navigator.serviceWorker.register(SW_PATH, {
        scope: '/',
      });
    } catch (err) {
      console.warn('[FCM] Service worker registration failed', err);
      return null;
    }
  }

  async getMessagingToken(): Promise<string | null> {
    const supported = await this.isSupported();
    if (!supported) {
      return null;
    }

    await this.registerServiceWorker();

    if (!this.#messaging) {
      this.#app = initializeApp(this.#config.firebaseConfig);
      this.#messaging = getMessaging(this.#app);
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return null;
      }

      const registration =
        (await navigator.serviceWorker.getRegistration('/')) ??
        (await this.registerServiceWorker());

      return await getToken(this.#messaging, {
        vapidKey: this.#config.firebaseVapiKey,
        serviceWorkerRegistration: registration ?? undefined,
      });
    } catch (err) {
      console.error('[FCM] Failed to obtain token', err);
      return null;
    }
  }

  listenForMessages(): void {
    if (this.#listening || !this.#messaging) {
      return;
    }
    this.#listening = true;

    onMessage(this.#messaging, (payload) => {
      const data = (payload.data ?? {}) as PushNotificationData;
      const title = data['pinpoint.notification.title'] ?? 'Notification';
      const body = data['pinpoint.notification.body'] ?? '';
      const deepLinkRaw = extractDeepLinkFromFcmData(data);

      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.#showForegroundNotification(title, body, data, deepLinkRaw);
      }
    });
  }

  #showForegroundNotification(
    title: string,
    body: string,
    data: PushNotificationData,
    deepLinkRaw: unknown,
  ): void {
    if (typeof Notification === 'undefined') {
      return;
    }

    const notification = new Notification(title, {
      body,
      icon: data['pinpoint.notification.imageIconUrl'] ?? '/favicon.ico',
      data: { deepLinkRaw },
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      const parsed = parseDeepLinkRaw(deepLinkRaw);
      if (parsed) {
        void this.#injector
          .get(NotificationNavigationService)
          .navigateWithDeepLinkData(parsed);
      }
    };
  }
}
